import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, Copy, Check, LifeBuoy, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Globale ErrorBoundary für PayPilot.
 *
 * Spec: docs/design-spec.md §7 — Error-Pattern.
 *   - Red Icon (64px) + h3 "Etwas ist schiefgelaufen"
 *   - Trace-ID (mono, copy-Button)
 *   - "Erneut versuchen" + "Support"
 *
 * Fängt unbehandelte Render-Fehler ab und zeigt statt einer weißen Seite
 * eine Recovery-UI. Im DEV-Modus wird der Stacktrace als <details> ausklappbar
 * angezeigt.
 *
 * Trace-ID: stabile Kennung pro Crash (gehasht aus Message + Timestamp), die
 * dem User zum Kopieren angeboten wird, damit der Support den Fehler im
 * Backend-Log finden kann.
 *
 * Optional wird der Fehler an `/api/v1/client-error` gemeldet — schlägt
 * der Endpoint fehl (z.B. weil er noch nicht existiert), wird der Fehler
 * stillschweigend verworfen, damit die Recovery-UI nicht selbst crasht.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optionales Label für Telemetry/Logs (z.B. "root", "route", "panel").
   * Hat keine UI-Auswirkung, hilft beim Debugging.
   */
  scope?: string;
  /**
   * Optionaler Custom-Fallback. Wenn nicht gesetzt, wird die Default-UI
   * genutzt.
   */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
  traceId: string | null;
  copied: boolean;
}

const SUPPORT_EMAIL = "support@kalkuel.de";

function generateTraceId(): string {
  // 12 hex chars — gut genug für eine User-readable Trace-ID.
  const ts = Date.now().toString(16);
  const rand = Math.random().toString(16).slice(2, 8);
  return `${ts.slice(-8)}-${rand}`.toLowerCase();
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
      componentStack: null,
      traceId: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error, traceId: generateTraceId() };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    const scope = this.props.scope ?? "root";
    const traceId = this.state.traceId ?? generateTraceId();
    // Konsole-Log mit klarem Marker — der Browser-Stack ist im DEV-Modus
    // ohnehin schon sichtbar, aber so ist die Quelle eindeutig.
    console.error(
      `[ErrorBoundary][${scope}][trace:${traceId}]`,
      error,
      "\nComponentStack:",
      info.componentStack,
    );

    this.setState({
      componentStack: info.componentStack ?? null,
      traceId,
    });

    // Bestmögliche Telemetrie: an Backend melden, falls Endpoint existiert.
    // Fehler werden geschluckt — die Recovery-UI darf NIE selbst crashen.
    void this.reportToTelemetry(scope, traceId, error, info.componentStack ?? "");
  }

  private async reportToTelemetry(
    scope: string,
    traceId: string,
    error: Error,
    componentStack: string,
  ): Promise<void> {
    try {
      const payload = {
        scope,
        trace_id: traceId,
        message: error.message,
        name: error.name,
        stack: error.stack ?? null,
        component_stack: componentStack,
        url: typeof window !== "undefined" ? window.location.href : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        timestamp: new Date().toISOString(),
      };

      // sendBeacon ist non-blocking und uebersteht auch Page-Unloads.
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.sendBeacon === "function"
      ) {
        const blob = new Blob([JSON.stringify(payload)], {
          type: "application/json",
        });
        navigator.sendBeacon("/api/v1/client-error", blob);
        return;
      }

      // Fallback: fetch mit keepalive — wir warten nicht auf die Response.
      await fetch("/api/v1/client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
        keepalive: true,
      });
    } catch {
      // Schlucken — Telemetrie ist Best-Effort.
    }
  }

  private handleReload = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  private handleGoHome = (): void => {
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  private handleCopyTrace = async (): Promise<void> => {
    const id = this.state.traceId;
    if (!id) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(id);
      } else {
        // Legacy-Fallback
        const ta = document.createElement("textarea");
        ta.value = id;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // Clipboard kann blockiert sein (z.B. iframe ohne permission) — silent.
    }
  };

  private handleSupport = (): void => {
    const id = this.state.traceId ?? "(unbekannt)";
    const subject = encodeURIComponent(`PayPilot — Fehler ${id}`);
    const body = encodeURIComponent(
      [
        "Hallo Support-Team,",
        "",
        "in PayPilot ist ein Fehler aufgetreten.",
        "",
        `Trace-ID: ${id}`,
        `URL: ${typeof window !== "undefined" ? window.location.href : "—"}`,
        `Zeitpunkt: ${new Date().toLocaleString("de-DE")}`,
        "",
        "Was ist passiert? (gerne ergänzen)",
      ].join("\n"),
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  render(): ReactNode {
    const { error, componentStack, traceId, copied } = this.state;

    if (!error) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    const isDev = import.meta.env.DEV;

    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center bg-bg-canvas p-6">
        <div className="mx-auto flex max-w-md flex-col items-center text-center">
          <div
            className={cn(
              "mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full p-4",
              "bg-status-rejected-bg text-status-rejected-text",
            )}
          >
            <AlertTriangle className="h-8 w-8" aria-hidden />
          </div>
          <h3 className="text-h3 text-text-primary">Etwas ist schiefgelaufen</h3>
          <p className="mt-2 text-small text-text-secondary">
            Die Seite ist auf einen unerwarteten Fehler gestoßen. Du kannst es
            erneut versuchen oder den Support kontaktieren.
          </p>

          {traceId && (
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-border-default bg-bg-subtle px-2 py-1.5">
              <span className="text-tiny text-text-tertiary">Trace-ID:</span>
              <span className="font-mono text-mono-sm text-text-primary tabular-nums">
                {traceId}
              </span>
              <button
                type="button"
                onClick={() => void this.handleCopyTrace()}
                aria-label="Trace-ID kopieren"
                className={cn(
                  "inline-flex h-5 w-5 items-center justify-center rounded-sm",
                  "text-text-tertiary transition-colors duration-fast ease-out-quint",
                  "hover:bg-bg-hover hover:text-text-primary focus-ring",
                )}
              >
                {copied ? (
                  <Check className="h-3 w-3 text-currency-positive" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            </div>
          )}

          <div className="mt-6 flex flex-col items-center gap-2 sm:flex-row sm:gap-3">
            <Button onClick={this.handleReload}>
              <RefreshCw className="h-4 w-4" />
              Erneut versuchen
            </Button>
            <Button variant="outline" onClick={this.handleSupport}>
              <LifeBuoy className="h-4 w-4" />
              Support kontaktieren
            </Button>
            <Button variant="ghost" onClick={this.handleGoHome}>
              <Home className="h-4 w-4" />
              Zur Startseite
            </Button>
          </div>

          {isDev ? (
            <details className="mt-8 w-full rounded-md border border-border-default bg-bg-subtle p-3 text-left text-xs">
              <summary className="cursor-pointer select-none font-medium text-text-secondary">
                Stacktrace (nur im DEV-Modus sichtbar)
              </summary>
              <div className="mt-3 space-y-3">
                <div>
                  <div className="font-medium text-text-primary">
                    {error.name}: {error.message}
                  </div>
                  {error.stack ? (
                    <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-text-tertiary">
                      {error.stack}
                    </pre>
                  ) : null}
                </div>
                {componentStack ? (
                  <div>
                    <div className="font-medium text-text-primary">
                      Component-Stack
                    </div>
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-text-tertiary">
                      {componentStack}
                    </pre>
                  </div>
                ) : null}
              </div>
            </details>
          ) : null}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
